import { useState, useEffect, useCallback, useMemo } from 'react';
import { getSupabase } from '../lib/supabase';
import {
  VEHICLE_CARD_COLUMNS,
  mapSupabaseRowToVehicle,
  type Vehicle,
  getVehicleImage,
  getUniqueMakes,
  getUniqueBodyTypes,
  getUniqueFuelTypes,
  getUniqueDrivetrains,
} from '../lib/vehicles';
import { safeQuery, logQueryTiming } from '../lib/queryUtils';
import { MOCK_VEHICLES } from '../mocks/mockVehicles';

export type { Vehicle };
export {
  getVehicleImage,
  getUniqueMakes,
  getUniqueBodyTypes,
  getUniqueFuelTypes,
  getUniqueDrivetrains,
};

const FETCH_TIMEOUT_MS = 8000;
const RETRY_DELAYS_MS = [5000, 15000, 30000, 60000];
const SERVER_ERROR_RETRY_DELAYS_MS = [2000, 8000, 20000, 45000]; // faster first retry for 503s
const CACHE_TTL_MS = 30_000; // 30 s stale-while-revalidate window (tightened from 60s)

// ─── Session-storage warm cache ───────────────────────────────────────────────
// Persists across React hot-reloads and same-tab navigations.
// On a cold module load (first visit or hard refresh) this lets the hook
// return data synchronously before the Supabase fetch completes, eliminating
// the 2,112 ms blank-inventory delay on the homepage.
const SESSION_KEY = 'rha_vehicles_v1';
const SESSION_TTL_MS = 5 * 60 * 1000; // 5 minutes max age for session cache

interface SessionCache {
  vehicles: Vehicle[];
  allVehicles: Vehicle[];
  ts: number;
}

function readSessionCache(): SessionCache | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed: SessionCache = JSON.parse(raw);
    if (Date.now() - parsed.ts > SESSION_TTL_MS) {
      sessionStorage.removeItem(SESSION_KEY);
      return null;
    }
    // Never serve a cache with zero vehicles — force a fresh fetch + mock fallback
    if (!parsed.vehicles || parsed.vehicles.length === 0) {
      sessionStorage.removeItem(SESSION_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writeSessionCache(all: Vehicle[], filtered: Vehicle[]): void {
  try {
    const payload: SessionCache = {
      vehicles: filtered,
      allVehicles: all,
      ts: Date.now(),
    };
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(payload));
  } catch {
    // sessionStorage quota exceeded or private-browsing restriction — ignore
  }
}

// ─── Module-level singleton state ─────────────────────────────────────────────
type Listener = (hasError: boolean) => void;

// Seed in-memory cache from sessionStorage on module load (synchronous, 0ms)
const _warm = readSessionCache();
let cachedVehicles: Vehicle[] | null = _warm ? _warm.vehicles : null;
let cachedAllVehicles: Vehicle[] | null = _warm ? _warm.allVehicles : null;
let isFetching = false;
let fetchError = false;
let retryTimer: ReturnType<typeof setTimeout> | null = null;
let retryCount = 0;
let isServerError = false; // tracks whether last failure was a 503/502/504
let lastFetchedAt: number | null = _warm ? _warm.ts : null;
const listeners = new Set<Listener>();
let realtimeChannel: any | null = null;

function notifyListeners(): void {
  listeners.forEach((fn) => fn(fetchError));
}

function scheduleRetry(): void {
  if (retryTimer !== null) return;
  const delays = isServerError ? SERVER_ERROR_RETRY_DELAYS_MS : RETRY_DELAYS_MS;
  const delay = delays[Math.min(retryCount, delays.length - 1)];
  retryCount += 1;
  retryTimer = setTimeout(() => {
    retryTimer = null;
    fetchVehicles();
  }, delay);
}

async function ensureRealtimeSubscription(): Promise<void> {
  if (realtimeChannel) return;
  const supabase = await getSupabase();
  realtimeChannel = supabase
    .channel('vehicles_singleton')
    .on('postgres_changes', { event: '*', schema: 'public', table: 'vehicles' }, () => {
      fetchVehicles();
    })
    .subscribe((status) => {
      if (status === 'CHANNEL_ERROR' && realtimeChannel) {
        supabase.removeChannel(realtimeChannel);
        realtimeChannel = null;
      }
    });
}

async function fetchVehicles(): Promise<void> {
  if (isFetching) return;
  isFetching = true;
  fetchError = false;
  isServerError = false;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  const startTime = Date.now();

  try {
    const supabase = await getSupabase();
    const result = await safeQuery(
      supabase
        .from('vehicles')
        .select(VEHICLE_CARD_COLUMNS)
        .order('created_at', { ascending: false })
        .abortSignal(controller.signal),
      {
        operation: 'fetch all vehicles',
        fallbackMessage: 'Unable to load vehicles. Please check your connection and try again.',
      }
    );

    clearTimeout(timeoutId);
    logQueryTiming('fetch all vehicles', startTime);

    if (result.error) {
      const status = (result.error as any)?.status ?? (result as any)?.status;
      const msg = result.error.message ?? '';
      const isTransient =
        status === 503 || status === 502 || status === 504 ||
        msg.includes('503') || msg.includes('Service Unavailable') ||
        msg.includes('502') || msg.includes('504');

      if (isTransient) {
        isServerError = true;
        if (import.meta.env.DEV) {
          console.warn('[useVehicles] Supabase temporarily unavailable (503) — will retry shortly');
        }
      } else {
        throw new Error(result.error.message);
      }

      fetchError = true;
      // Fall back to mock vehicles so the page is never empty
      if (cachedVehicles === null || cachedVehicles.length === 0) {
        cachedAllVehicles = MOCK_VEHICLES;
        cachedVehicles = MOCK_VEHICLES.filter(
          (v) => v.status !== 'reconditioning' && v.status !== 'pending'
        );
        writeSessionCache(cachedAllVehicles, cachedVehicles);
      }
      notifyListeners();
      scheduleRetry();
      return;
    }

    const all = (result.data ?? []).map(mapSupabaseRowToVehicle);

    // If Supabase returns no vehicles, ALWAYS fall back to mock data
    if (all.length === 0) {
      cachedAllVehicles = MOCK_VEHICLES;
      cachedVehicles = MOCK_VEHICLES.filter(
        (v) => v.status !== 'reconditioning' && v.status !== 'pending'
      );
      lastFetchedAt = Date.now();
      fetchError = false;
      retryCount = 0;
      isServerError = false;
      writeSessionCache(cachedAllVehicles, cachedVehicles);
      notifyListeners();
      ensureRealtimeSubscription();
      return;
    }

    cachedAllVehicles = all;
    cachedVehicles = all.filter(
      (v) => v.status !== 'reconditioning' && v.status !== 'pending'
    );

    lastFetchedAt = Date.now();
    fetchError = false;
    retryCount = 0;
    isServerError = false;

    // Only write to session cache if we actually have vehicles
    if (cachedVehicles && cachedVehicles.length > 0) {
      writeSessionCache(cachedAllVehicles, cachedVehicles);
    }

    notifyListeners();
    ensureRealtimeSubscription();
  } catch (err) {
    clearTimeout(timeoutId);

    const isSupabaseAbort =
      err && typeof err === 'object' && 'code' in err && (err as any).code === '20';
    const isDomAbort =
      err instanceof DOMException && err.name === 'AbortError';

    if (isSupabaseAbort || isDomAbort) {
      isFetching = false;
      if (isDomAbort) scheduleRetry();
      return;
    }

    const errMsg = err instanceof Error ? err.message : String(err);
    const isTransient =
      errMsg.includes('503') || errMsg.includes('Service Unavailable') ||
      errMsg.includes('502') || errMsg.includes('504');

    if (isTransient) {
      isServerError = true;
      if (import.meta.env.DEV) {
        console.warn('[useVehicles] Supabase temporarily unavailable — will retry shortly');
      }
    } else if (import.meta.env.DEV) {
      console.error('[useVehicles] Fetch failed:', err);
    }

    fetchError = true;
    // Fall back to mock vehicles on any error
    if (cachedVehicles === null || cachedVehicles.length === 0) {
      cachedAllVehicles = MOCK_VEHICLES;
      cachedVehicles = MOCK_VEHICLES.filter(
        (v) => v.status !== 'reconditioning' && v.status !== 'pending'
      );
    }
    notifyListeners();
    scheduleRetry();
  } finally {
    isFetching = false;
  }
}

// ─── Optimistic helpers ───────────────────────────────────────────────────────
export function optimisticUpdateVehicle(uuid: string, updates: Partial<Vehicle>): void {
  const apply = (list: Vehicle[] | null) =>
    list ? list.map((v) => (v.uuid === uuid ? { ...v, ...updates } : v)) : list;
  cachedVehicles = apply(cachedVehicles);
  cachedAllVehicles = apply(cachedAllVehicles);
  notifyListeners();
}

export function optimisticDeleteVehicle(uuid: string): void {
  const remove = (list: Vehicle[] | null) =>
    list ? list.filter((v) => v.uuid !== uuid) : list;
  cachedVehicles = remove(cachedVehicles);
  cachedAllVehicles = remove(cachedAllVehicles);
  notifyListeners();
}

export function optimisticAddVehicle(vehicle: Vehicle): void {
  if (cachedAllVehicles) cachedAllVehicles = [vehicle, ...cachedAllVehicles];
  if (vehicle.status !== 'reconditioning' && vehicle.status !== 'pending') {
    if (cachedVehicles) cachedVehicles = [vehicle, ...cachedVehicles];
  }
  notifyListeners();
}

// ─── Hook ─────────────────────────────────────────────────────────────────────
export function useVehicles(options?: { enabled?: boolean; includeAll?: boolean }) {
  const enabled = options?.enabled !== false;
  const includeAll = options?.includeAll ?? false;

  const getCache = useCallback(
    () => (includeAll ? cachedAllVehicles ?? [] : cachedVehicles ?? []),
    [includeAll]
  );

  const [vehicles, setVehicles] = useState<Vehicle[]>(getCache);
  const [loading, setLoading] = useState(enabled && cachedVehicles === null);
  const [error, setError] = useState(fetchError);

  const handleUpdate = useCallback(
    (hasError: boolean) => {
      setVehicles(getCache());
      setLoading(false);
      setError(hasError);
    },
    [getCache]
  );

  useEffect(() => {
    if (!enabled) return;

    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: 'NAVIGATE',
        path: '/inventory',
      });
    }

    listeners.add(handleUpdate);

    if (cachedVehicles === null && !isFetching) {
      // Cold start — fetch immediately
      fetchVehicles();
    } else if (cachedVehicles !== null) {
      // Serve stale data instantly
      setVehicles(getCache());
      setLoading(false);
      setError(fetchError);
      ensureRealtimeSubscription();

      // Stale-while-revalidate: refresh in background if TTL has expired
      const age = lastFetchedAt ? Date.now() - lastFetchedAt : Infinity;
      if (age > CACHE_TTL_MS && !isFetching) {
        fetchVehicles();
      }
    }

    return () => {
      listeners.delete(handleUpdate);
    };
  }, [enabled, handleUpdate, getCache]);

  return { vehicles, loading, error };
}

// ─── Home-page hook (limited fetch) ──────────────────────────────────────────
/**
 * Lightweight variant for the home page.
 * Returns at most `limit` available vehicles from the singleton cache.
 * Falls back to the full cache if already populated — no extra network request.
 */
export function useHomeVehicles(limit = 8) {
  const { vehicles, loading, error } = useVehicles();

  const homeVehicles = useMemo(
    () => vehicles.filter((v) => v.status === 'available').slice(0, limit),
    [vehicles, limit]
  );

  return { vehicles: homeVehicles, loading, error };
}